import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from 'src/environments/environment';
import {forkJoin, map, Observable} from 'rxjs';
import {Stage, StageMetadata} from '../../model/book-model';
import {
  DRM_PLATFORM_CALLAN_CASA,
  V3DeviceRegistration,
  V3DeviceRegistrationRequest,
  V3DrmAccount,
  V3EncryptionKeyValue,
} from '../../model/v3/drm';
import {Page} from '../../../utils/pageable';

/**
 * v3 counterpart of `BookReaderApiService`. All calls go through the API Gateway
 * (events-server, `EventsDrm` contract) instead of the legacy app-server endpoints.
 *
 * Legacy -> v3 mapping:
 * - `/api/products/...`                  -> `GET /api/v1/products/metadata|content|resources?path=...` (path as request param)
 * - `POST /api/device/device-key`        -> `getAccountForCasaAccount` + `registerDeviceV2`
 * - `POST /api/device/encryption-key`    -> `createProductKey`
 */
@Injectable({
  providedIn: 'root'
})
export class BookReaderApiV3Service {

  constructor(
    private http: HttpClient
  ) { }

  private productsUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/products${path}`;
  }

  private accountsUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/accounts${path}`;
  }

  /**
   * Raw metadata for the first product of `path`. Used by the reader page to read `ver` and
   * pick the matching reader component. The shape differs by version (Stage for ver 1,
   * BookMetadataV2 for ver 2), so it is returned untyped and cast by the caller.
   */
  getMetadata(path: string): Observable<any> {
    const productPath = path.split(";")[0].split("?")[0]
    return this.http.get<any>(this.productsUrl(`/metadata`), {
      params: {source: 'repo', path: productPath}
    })
  }

  /**
   * ver 2 content fetch. The backend /content endpoint accepts a String `anchor` (e.g. "int-1",
   * "pron_chart_1", or a numeric page).
   */
  getContentV2(anchor: string | number, path: string, size: number, XDeviceKey: string, XEncryptionKey: string): Observable<string> {
    const pathParams = path.split("?")
    return this.http.get<string>(this.productsUrl(`/content`),
      {
        headers: {
          'X-Device-Key': XDeviceKey,
          'X-Encryption-Key': XEncryptionKey
        },
        params: {
          source: 'repo',
          path: pathParams[0],
          anchor: anchor as any,
          size: size,
          variant: pathParams[1]?.split("=")[1]
        },
        responseType: 'text' as 'json'
      })
  }

  getEbookAnchors(path: string): Observable<StageMetadata[]> {
    let observables = path.split(";").map(path => {
        let productPath = path.split("?")[0]
        return this.http.get<Stage>(this.productsUrl(`/metadata`), {
          params: {source: 'repo', path: productPath}
        }).pipe(
          map(stage => {
            let res = new StageMetadata()
            res.stage = stage
            res.path = path
            res.start = Math.min(...stage["content-items"].map(it => it.anchors.start))
            res.end = Math.max(...stage["content-items"].map(it => it.anchors.end))
            return res
          })
        )
      })

    return forkJoin(observables)
  }

  getContent(anchor: number, path: string, size: number, XDeviceKey: string, XEncryptionKey: string): Observable<string> {
    const pathParams = path.split("?")
    return this.http.get<string>(this.productsUrl(`/content`),
        {
          headers: {
            'X-Device-Key': XDeviceKey,
            'X-Encryption-Key': XEncryptionKey
          },
          params: {
            source: 'repo',
            path: pathParams[0],
            anchor: anchor,
            size: size,
            variant: pathParams[1].split("=")[1]
          },
          responseType : 'text' as 'json'
        })
  }

  getResource(path: string, resourcePath: string): Observable<Blob> {
    const productPath = path?.split("?")[0]
    return this.http.get(this.productsUrl(`/resources`), {
      params: {source: 'repo', path: productPath, resourcePath: resourcePath},
      responseType: 'blob'
    })
  }

  /**
   * Resolves the DRM account for the logged-in person. DRM keeps its own account id space,
   * so the CASA account id has to be translated before any account-scoped call.
   */
  getAccountForCasaAccount(casaAccountId: number | string): Observable<V3DrmAccount> {
    const params = new HttpParams()
      .set('platform', DRM_PLATFORM_CALLAN_CASA)
      .set('id', `${casaAccountId}`)
    return this.http.get<Page<V3DrmAccount>>(this.accountsUrl(), {params}).pipe(
      map(page => page.content?.[0] ?? null)
    )
  }

  /**
   * v3 replacement of `generateDeviceKey`: registers the browser as a DRM device
   * using the v2 protocol. The returned `deviceKey` is the `X-Device-Key` token and
   * `deviceId` is required by `createProductKey`; both must be persisted by the caller.
   */
  registerDeviceV2(accountId: number, request: V3DeviceRegistrationRequest): Observable<V3DeviceRegistration> {
    return this.http.post<V3DeviceRegistration>(
      this.accountsUrl(`/${accountId}/devices`), request, {params: {protocol: 'v2'}}
    )
  }

  /**
   * v3 replacement of `generateEncryptionKey`: ECDH key agreement for reader content.
   * `webSharedKey` is the Base64 client public key. The response carries the short-lived
   * `X-Encryption-Key` token (`key`) and the server public key (`publicServerKey`) —
   * note the field names differ from the legacy `EncryptionResponse`
   * (`encryptionKey`/`publicKeyServer`).
   */
  createProductKey(accountId: number, deviceId: string, XDeviceKey: string, webSharedKey: string): Observable<V3EncryptionKeyValue> {
    return this.http.post<V3EncryptionKeyValue>(
      this.accountsUrl(`/${accountId}/devices/${deviceId}/product-keys`), webSharedKey,
      {
        headers: {
          'X-Device-Key': XDeviceKey
        }
      }
    )
  }

}
